import { ClientPlan } from '@maru44/huntre-utils/src/models/client'
import type { ClientDeviceWithDevice } from '@maru44/huntre-utils/src/models/clientDevice'
import { ClientMemberRole } from '@maru44/huntre-utils/src/models/clientMember'
import { Box, Button, Stack, Typography } from '@mui/material'
import { useCallback, useContext, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Breads } from 'src/components/atoms/BreadCrumbs'
import { CreateClientDeviceDialog } from 'src/components/dialogs/device/CreateClientDeviceDialog'
import { ClientContext } from 'src/components/providers/ClientProvider'
import { buybackDeviceTypeIds } from 'src/config'
import { useClientDevices } from 'src/hooks/clientDevice/useClientDevices'
import { ClientDeviceTable } from 'src/pages/ClientDevices/DeviceTable'
import { ErrorPage } from '../ErrorPage'
import { DevicePositionsMap } from './DevicePositionsMap'

export const ClientDevices = () => {
  const { client, currentMember, clientSubGroups } = useContext(ClientContext)

  const { listClientDevicesWithDevice, createClientDevice, updateIsTurnedOff } = useClientDevices(client?.id ?? '')

  const [openCreate, setOpenCreate] = useState(false)

  const handleCreateClientDevice = useCallback(
    async ({ deviceId }: { deviceId: string }) => {
      await createClientDevice({
        deviceId,
      })
    },
    [createClientDevice, client?.id],
  )

  const visibleClientDevices = useMemo((): ClientDeviceWithDevice[] | undefined => {
    if (!listClientDevicesWithDevice.data) return undefined
    if (currentMember?.role === ClientMemberRole.Admin) return listClientDevicesWithDevice.data

    if (clientSubGroups.length === 0 || client?.plan !== ClientPlan.Pro) {
      return listClientDevicesWithDevice.data
    }

    const memberId = currentMember?.id ?? ''
    const mySubGroups = clientSubGroups.filter((sg) => sg.clientMemberIds.includes(memberId))
    const allowedDeviceIds = new Set(mySubGroups.flatMap((sg) => sg.clientDeviceIds))
    return listClientDevicesWithDevice.data.filter((v) => allowedDeviceIds.has(v.clientDevice.id))
  }, [listClientDevicesWithDevice.data, clientSubGroups, currentMember, client?.plan])

  const positions = useMemo((): ClientDeviceWithDevice[] => {
    if (!visibleClientDevices) {
      return []
    }
    return visibleClientDevices.filter((v) => !!v.device.position || !!v.clientDevice.position)
  }, [visibleClientDevices])

  return (
    <>
      <Box>
        <Breads breadIds={['home']} />
        <Stack spacing={2}>
          <Typography variant="h5">デバイス一覧</Typography>
          <Typography>登録したデバイス一覧です</Typography>
          {visibleClientDevices && (
            <>
              <ClientDeviceTable
                clientDevices={visibleClientDevices}
                clientSubGroups={client?.plan === ClientPlan.Pro ? clientSubGroups : []}
                onUpdateIsTurnedOff={updateIsTurnedOff}
                mutate={listClientDevicesWithDevice.mutate}
              />
              {currentMember?.role === ClientMemberRole.Admin && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'end',
                  }}
                >
                  <Button variant="contained" onClick={() => setOpenCreate(true)}>
                    デバイスの登録
                  </Button>
                </Box>
              )}
              {buybackDeviceTypeIds.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'end',
                  }}
                >
                  <Button component={Link} to={`/clients/${client?.id}/buybacks`} variant="outlined">
                    デバイスの買取
                  </Button>
                </Box>
              )}
              {positions.length > 0 && (
                <Box>
                  <Typography variant="h6">位置情報</Typography>
                  <DevicePositionsMap devices={positions} />
                </Box>
              )}
            </>
          )}
          {listClientDevicesWithDevice.error && <ErrorPage exception={listClientDevicesWithDevice.error} />}
        </Stack>
      </Box>
      <CreateClientDeviceDialog
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        listMutate={listClientDevicesWithDevice.mutate}
        onSubmit={handleCreateClientDevice}
      />
    </>
  )
}
